import type { FC } from "react";
import { HelpCircle } from "@untitledui/icons";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

interface MetricCardProps {
    icon: FC<{ className?: string }>;
    label: string;
    value: string;
    /** Optional help text. When provided a "?" icon appears top-right with this string in a tooltip. */
    tooltip?: string;
    className?: string;
    /** Custom attribute for E2E testing */
    "data-autotest"?: string;
}

/**
 * Single-row stat card: featured icon on the left, label+value on the right.
 * Designed for dashboard summary strips (e.g. Schedule's Rate Unadjusted).
 */
export const MetricCard = ({ icon, label, value, tooltip, className, "data-autotest": dataAutotest }: MetricCardProps) => {
    return (
        <div data-autotest={dataAutotest} className={cx("relative flex items-center gap-4 rounded-xl border border-secondary bg-primary px-5 py-4 shadow-xs", className)}>
            <FeaturedIcon icon={icon} color="brand" theme="modern" size="lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-sm font-semibold text-tertiary">{label}</span>
                <span className="truncate text-display-xs font-semibold text-primary">{value}</span>
            </div>
            {tooltip && (
                <Tooltip title={tooltip} placement="top" arrow>
                    <TooltipTrigger
                        aria-label="Help"
                        className="absolute top-3 right-3 inline-flex size-5 items-center justify-center rounded-md text-fg-quaternary outline-hidden hover:text-fg-quaternary_hover focus-visible:ring-2 focus-visible:ring-focus-ring"
                    >
                        <HelpCircle className="size-4" />
                    </TooltipTrigger>
                </Tooltip>
            )}
        </div>
    );
};
